import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger
} from "@radix-ui/react-collapsible"
import {
	HomeIcon,
	BookOpenIcon,
	BotIcon,
	ChevronRightIcon,
	CircleDotIcon,
	ClipboardListIcon,
	LightbulbIcon,
	FolderKanbanIcon,
	PlusIcon,
	SettingsIcon,
	SirenIcon
} from "lucide-react"
import { useLocation } from "react-router-dom"
import { compareNames } from "~/lib/utils"
import { TutorialDiscovery } from "~/components/tutorials/TutorialDiscovery"
import { Link } from "~/components/Link"
import {
	SidebarGroup,
	SidebarGroupAction,
	SidebarGroupLabel,
	SidebarMenu,
	SidebarMenuBadge,
	SidebarMenuButton,
	SidebarMenuItem,
	SidebarMenuSub,
	SidebarMenuSubButton,
	SidebarMenuSubItem
} from "~/components/ui/sidebar"
import { EntityIdentity } from "~/components/EntityIdentity"
import { useOrganization } from "~/hooks/useOrganization"
import { usePermission } from "~/hooks/usePermission"
import { OrgPermissions, TeamPermissions } from "~/lib/permissions"
import type { RouterOutputs } from "~/trpc/react"
import { TeamCreateDialog } from "../screens/TeamCreateDialog"

export function NavTeams({
	data
}: {
	data: RouterOutputs["user"]["getTeams"]
}) {
	const pathname = useLocation().pathname
	const { organization } = useOrganization()
	const { hasOrgPermission } = usePermission()
	const canCreateTeams = hasOrgPermission(OrgPermissions.CREATE_TEAMS)

	return (
		<SidebarGroup className="group-data-[collapsible=icon]:hidden">
			<SidebarGroupLabel variant="section">Teams</SidebarGroupLabel>
			{canCreateTeams && (
				<TeamCreateDialog>
					<SidebarGroupAction
						type="button"
						title="Create team"
						className="absolute"
					>
						<PlusIcon />
					</SidebarGroupAction>
				</TeamCreateDialog>
			)}
			<SidebarMenu>
				{[...data].sort(compareNames).map((team) => (
					<Collapsible
						key={team.name}
						asChild
						defaultOpen={pathname.startsWith(
							`/${organization?.slug}/${team.slug}`
						)}
						className="group/collapsible"
					>
						<SidebarMenuItem>
							<CollapsibleTrigger asChild>
								<SidebarMenuButton tooltip={team.name}>
									<EntityIdentity
										kind="team"
										name={team.name}
										image={team.logo}
										size="md"
										className="grow"
									/>
									<ChevronRightIcon className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
								</SidebarMenuButton>
							</CollapsibleTrigger>

							<CollapsibleContent>
								<SidebarMenuSub className="border-0">
									{[
										{
											label: "Home",
											icon: HomeIcon,
											suffix: "/home",
											visible: team.enableHome
										},
										{
											label: "Queue",
											icon: SirenIcon,
											suffix: "/queue",
											visible: team.enableIssues && team.enableQueue,
											count: team.queueCount
										},
										{
											label: "Issues",
											icon: CircleDotIcon,
											suffix: "/issues",
											visible: team.enableIssues,
											count: team.issueCount
										},
										{
											label: "Projects",
											icon: FolderKanbanIcon,
											suffix: "/projects",
											visible: team.enableProjects,
											prefix: true
										},
										{
											label: "Automations",
											icon: BotIcon,
											suffix: "/automations",
											visible: team.automationsFeatureEnabled,
											prefix: true
										},
										{
											label: "Suggestions",
											icon: LightbulbIcon,
											suffix: "/suggestions",
											visible: team.suggestionsEnabled,
											count: team.suggestionCount
										},
										{
											label: "Knowledge Base",
											icon: BookOpenIcon,
											suffix: "/kb",
											visible: team.enableKnowledgeBase
										},
										{
											label: "Forms",
											icon: ClipboardListIcon,
											suffix: "/forms",
											visible: team.enableForms
										}
									]
										.filter((item) => item.visible)
										.map(({ label, icon: Icon, suffix, count, prefix }) => {
											const to = `/${organization?.slug}/${team.slug}${suffix}`
											const navigation = (
												<SidebarMenuSubButton
													asChild
													isActive={
														prefix ? pathname.startsWith(to) : pathname === to
													}
												>
													<Link to={to}>
														<Icon />
														<span className="grow">{label}</span>
														{count !== undefined && count > 0 && (
															<SidebarMenuBadge>{count}</SidebarMenuBadge>
														)}
													</Link>
												</SidebarMenuSubButton>
											)

											return (
												<SidebarMenuSubItem key={label}>
													{suffix === "/automations" ||
													suffix === "/queue" ||
													suffix === "/projects" ? (
														<TutorialDiscovery
															tutorialId={
																suffix === "/queue"
																	? "queue-v1"
																	: suffix === "/projects"
																		? "projects-v1"
																		: "automations-v1"
															}
															destination={
																suffix === "/automations" ? `${to}/new` : to
															}
															layout="sidebar"
														>
															{navigation}
														</TutorialDiscovery>
													) : (
														navigation
													)}
												</SidebarMenuSubItem>
											)
										})}
									<TeamSettingsLink team={team} />
								</SidebarMenuSub>
							</CollapsibleContent>
						</SidebarMenuItem>
					</Collapsible>
				))}
			</SidebarMenu>
		</SidebarGroup>
	)
}

function TeamSettingsLink({
	team
}: {
	team: RouterOutputs["user"]["getTeams"][number]
}) {
	const pathname = useLocation().pathname
	const { organization } = useOrganization()
	const { hasTeamPermission } = usePermission({ activeTeamId: team.id })
	// Match the settings tabs, including Labels disappearing when Issues is disabled.
	const canAccessSettings = [
		TeamPermissions.MANAGE_TEAM,
		TeamPermissions.MANAGE_TEAM_MEMBERS,
		TeamPermissions.ADMINISTRATOR,
		TeamPermissions.VIEW_AUDIT_LOG,
		...(team.enableIssues ? [TeamPermissions.MANAGE_LABELS] : [])
	].some(hasTeamPermission)
	if (
		!organization?.teams.some((candidate) => candidate.id === team.id) ||
		!canAccessSettings
	)
		return null
	const to = `/${organization?.slug}/${team.slug}/settings`
	return (
		<SidebarMenuSubItem>
			<SidebarMenuSubButton asChild isActive={pathname === to}>
				<Link to={to}>
					<SettingsIcon />
					<span className="grow">Settings</span>
				</Link>
			</SidebarMenuSubButton>
		</SidebarMenuSubItem>
	)
}
